import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { MapContainer, TileLayer, Marker, Tooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { priceColor } from "../utils/priceColor";
import { stationIcon } from "../utils/stationIcon";
import { TILE_LAYER_ATTRIBUTION, useTileLayerUrl } from "../mapTiles";

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

// Continental-US center/zoom -- every demo station sits inside this view,
// so there's no reason to compute bounds dynamically yet.
const DEFAULT_CENTER = [37.5, -96.5];
const DEFAULT_ZOOM = 4.5;

export default function FuelMap() {
  const tileUrl = useTileLayerUrl();
  const [prices, setPrices] = useState([]);
  const [truckStops, setTruckStops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // state_code -> $/gal diesel excise tax rate, so each station's tooltip
  // can show price/tax/pre-tax price instead of only the all-in price.
  const [taxByState, setTaxByState] = useState({});

  useEffect(() => {
    const token = localStorage.getItem("token");
    const headers = { headers: { Authorization: `Bearer ${token}` } };
    Promise.all([
      axios.get(`${apiBaseUrl}/fleet/latest-fuel-prices`, headers),  // current price per station (not the 39k-row history)
      axios.get(`${apiBaseUrl}/truck_stops/?limit=5000`, headers),
    ])
      .then(([priceRes, stopRes]) => {
        setPrices(priceRes.data.records || priceRes.data || []);
        setTruckStops((stopRes.data.records || stopRes.data || []).filter((s) => s.lat != null));
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));

    // Independent, best-effort -- same reasoning as DispatcherBoard.jsx's
    // own fetch of this table: a tax breakdown is a nice-to-have, never
    // something that should take the whole map down if unreachable.
    axios
      .get(`${apiBaseUrl}/state_fuel_tax_rates/?limit=500`, headers)
      .then((res) => {
        const rows = res.data.records || res.data || [];
        const byState = {};
        rows.forEach((r) => {
          if (r.state_code && r.diesel_tax_rate_per_gallon != null) {
            byState[r.state_code] = Number(r.diesel_tax_rate_per_gallon);
          }
        });
        setTaxByState(byState);
      })
      .catch(() => {});
  }, []);

  // fuel_prices has no lat/lng of its own -- real position comes from the
  // truck_stops row it references via truck_stop_id. Same join
  // DispatcherBoard.jsx's own fuel-stop map already uses; stations here
  // means "a fuel_prices row with a resolvable position," not every truck
  // stop (a stop with no priced fuel_prices row yet just doesn't show a
  // price marker -- nothing to plot without a price).
  const stations = useMemo(() => {
    const stopById = {};
    truckStops.forEach((ts) => { stopById[ts.id] = ts; });
    return prices
      .map((p) => {
        const stopRef = p.truck_stop_id;
        const stopId = stopRef?.id || stopRef;
        const stop = stopId ? stopById[stopId] : null;
        if (!stop) return null;
        return { ...p, lat: stop.lat, lng: stop.lng, station_name: stop.name, city: stop.city, state: stop.state };
      })
      .filter(Boolean);
  }, [prices, truckStops]);

  const { min, max, avg } = useMemo(() => {
    const values = stations.map((s) => Number(s.price_per_gallon)).filter((p) => !isNaN(p));
    if (!values.length) return { min: 0, max: 0, avg: 0 };
    const sum = values.reduce((a, b) => a + b, 0);
    return { min: Math.min(...values), max: Math.max(...values), avg: sum / values.length };
  }, [stations]);

  // Real prices here are only ever refreshed by someone manually running
  // fuel_price_import.py by hand -- there's no scheduled job, no live feed
  // (sync_from_api is an intentional NotImplementedError stub). Every cost
  // calculation in the platform (recommend_fuel_stop, trip cost estimates,
  // fuel_tax_report) silently uses whatever was last imported, however old
  // that is, with nothing telling anyone. Surfacing the real newest
  // price_date here is the one place a dispatcher/admin can actually see
  // that before trusting a number built on it.
  const { mostRecentPriceDate, daysSincePriceUpdate } = useMemo(() => {
    const dates = stations.map((s) => s.price_date).filter(Boolean).sort();
    const mostRecent = dates.length ? dates[dates.length - 1] : null;
    const days = mostRecent ? Math.floor((Date.now() - new Date(mostRecent).getTime()) / 86400000) : null;
    return { mostRecentPriceDate: mostRecent, daysSincePriceUpdate: days };
  }, [stations]);
  const STALE_PRICE_DAYS = 14;

  return (
    <div>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Fuel Prices</h1>
          <div style={styles.subtitle}>
            Diesel prices at {stations.length} truck stops
            {stations.length > 0 && (
              <>
                {" "}· cheapest <strong style={styles.good}>${min.toFixed(2)}</strong> · avg $
                {avg.toFixed(2)} · priciest <strong style={styles.bad}>${max.toFixed(2)}</strong>
              </>
            )}
          </div>
          {mostRecentPriceDate && (
            <div
              style={{
                ...styles.subtitle,
                marginTop: 2,
                color: daysSincePriceUpdate > STALE_PRICE_DAYS ? "#b45309" : "var(--muted)",
                fontWeight: daysSincePriceUpdate > STALE_PRICE_DAYS ? 700 : 400,
              }}
            >
              Prices last updated{" "}
              {daysSincePriceUpdate === 0 ? "today" : `${daysSincePriceUpdate} day${daysSincePriceUpdate === 1 ? "" : "s"} ago`}
              {" "}({mostRecentPriceDate})
              {daysSincePriceUpdate > STALE_PRICE_DAYS &&
                " — no automatic refresh exists yet; these may be stale until someone re-runs the import"}
            </div>
          )}
        </div>
      </div>

      {loading && <div style={styles.state}>Loading fuel prices…</div>}
      {error && <div style={{ ...styles.state, color: "#dc2626" }}>Couldn't load fuel prices: {error}</div>}
      {!loading && !error && stations.length === 0 && (
        <div style={styles.state}>No priced truck stops yet.</div>
      )}

      {!loading && !error && stations.length > 0 && (
        <div style={styles.mapCard}>
          <MapContainer center={DEFAULT_CENTER} zoom={DEFAULT_ZOOM} style={styles.map} scrollWheelZoom={true}>
            {tileUrl && <TileLayer attribution={TILE_LAYER_ATTRIBUTION} url={tileUrl} />}
            {stations.map((s) => (
              <Marker
                key={s.id}
                position={[Number(s.lat), Number(s.lng)]}
                icon={stationIcon(priceColor(Number(s.price_per_gallon), min, max))}
              >
                <Tooltip direction="top" offset={[0, -12]} opacity={1}>
                  <div style={styles.tipInner}>
                    <div style={styles.tipStation}>{s.station_name}</div>
                    <div style={styles.tipMeta}>
                      {s.brand} · {s.city}, {s.state}
                    </div>
                    {(() => {
                      const price = Number(s.price_per_gallon);
                      // price_per_gallon is the real pump price -- state
                      // fuel excise tax is already baked into it, not
                      // added on top, same as what a driver actually pays.
                      const tax = s.state ? taxByState[s.state] : null;
                      if (tax == null) {
                        return (
                          <div style={styles.tipPrice}>
                            ${price.toFixed(2)}/gal <span style={styles.tipFuelType}>{s.fuel_type}</span>
                          </div>
                        );
                      }
                      const preTax = price - tax;
                      return (
                        <div style={styles.tipPrice}>
                          <div>${price.toFixed(2)}/gal <span style={styles.tipFuelType}>{s.fuel_type}</span></div>
                          <div style={styles.tipTaxLine}>Tax: ${tax.toFixed(2)}/gal · Without tax: ${preTax.toFixed(2)}/gal</div>
                        </div>
                      );
                    })()}
                    <div style={styles.tipDate}>as of {s.price_date}</div>
                  </div>
                </Tooltip>
              </Marker>
            ))}
          </MapContainer>
        </div>
      )}
    </div>
  );
}

const styles = {
  header: { marginBottom: 16 },
  title: { fontSize: 22, fontWeight: 800, color: "var(--ink)", margin: 0 },
  subtitle: { fontSize: 13, color: "var(--muted)", marginTop: 4 },
  good: { color: "#16a34a" },
  bad: { color: "#dc2626" },
  state: {
    color: "var(--muted)", fontSize: 13, padding: 24, textAlign: "center",
    background: "var(--card)", border: "1px solid var(--line)", borderRadius: "var(--radius-lg)",
  },
  mapCard: {
    height: 560, borderRadius: "var(--radius-lg)", overflow: "hidden", border: "1px solid var(--line)",
    boxShadow: "var(--shadow-xs)",
  },
  map: { width: "100%", height: "100%" },
  tipInner: { fontSize: 12.5, lineHeight: 1.4, fontFamily: "inherit" },
  tipStation: { fontWeight: 700 },
  tipMeta: { color: "#6b7280" },
  tipPrice: { fontWeight: 700, marginTop: 2 },
  tipFuelType: { fontWeight: 400, color: "#6b7280", textTransform: "capitalize" },
  tipTaxLine: { fontWeight: 400, color: "#6b7280", fontSize: 11, marginTop: 1 },
  tipDate: { color: "#6b7280", marginTop: 2, fontSize: 11 },
};
